import { createFileRoute } from "@tanstack/react-router";
import { HeroSection } from "@/components/sections/HeroSection";
import { IdentificacaoSection } from "@/components/sections/IdentificacaoSection";
import { StickyMobileCTA } from "@/components/StickyMobileCTA";
import { TopBar } from "@/components/TopBar";
import { ScrollProgress } from "@/components/ScrollProgress";

import { lazy, Suspense, useEffect } from "react";
import { captureUtmsOnce } from "@/lib/utm";
import heroWebp480 from "@/assets/dr-ulisses-hero-480.webp.asset.json";
import heroWebp720 from "@/assets/dr-ulisses-hero-720.webp.asset.json";
import heroWebp960 from "@/assets/dr-ulisses-hero-960.webp.asset.json";

// Seções abaixo da dobra — carregam sob demanda para reduzir JS inicial.
const OfertaSection = lazy(() =>
  import("@/components/sections/OfertaSection").then((m) => ({ default: m.OfertaSection })),
);
const CondicoesSection = lazy(() =>
  import("@/components/sections/CondicoesSection").then((m) => ({ default: m.CondicoesSection })),
);
const ComoFuncionaSection = lazy(() =>
  import("@/components/sections/ComoFuncionaSection").then((m) => ({ default: m.ComoFuncionaSection })),
);
const TelemedicinaSection = lazy(() =>
  import("@/components/sections/TelemedicinaSection").then((m) => ({ default: m.TelemedicinaSection })),
);
const SobreSection = lazy(() =>
  import("@/components/sections/SobreSection").then((m) => ({ default: m.SobreSection })),
);
const AvaliacoesSection = lazy(() =>
  import("@/components/sections/AvaliacoesSection").then((m) => ({ default: m.AvaliacoesSection })),
);
const FaqSection = lazy(() =>
  import("@/components/sections/FaqSection").then((m) => ({ default: m.FaqSection })),
);
const FooterSection = lazy(() =>
  import("@/components/sections/FooterSection").then((m) => ({ default: m.FooterSection })),
);

const TITLE = "Dr. Ulisses Contente | Atendimento Médico em Saúde Mental Online";
const DESCRIPTION =
  "Consulta particular por telemedicina, com avaliação estendida e acompanhamento contínuo com o mesmo médico. Agenda limitada.";
const CANONICAL = "https://ulissescontente.lovable.app/";

export const Route = createFileRoute("/")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:url", content: CANONICAL },
    ],
    links: [
      { rel: "canonical", href: CANONICAL },
      // Preload da foto do hero SÓ no desktop (`media`), onde ela é o elemento de
      // LCP. No mobile o LCP é o H1 de texto e a foto fica bem abaixo da dobra —
      // ali o preload apenas disputava banda com as webfonts que o H1 precisa.
      {
        rel: "preload",
        as: "image",
        media: "(min-width: 768px)",
        href: heroWebp720.url,
        imageSrcSet: `${heroWebp480.url} 480w, ${heroWebp720.url} 720w, ${heroWebp960.url} 960w`,
        imageSizes: "(min-width: 768px) 420px, 360px",
        // camelCase: com `fetchpriority` o React 19 avisa "Invalid DOM property".
        fetchPriority: "high",
      } as unknown as { rel: string; href: string },
    ],
  }),
  component: LandingPage,
});

function LandingPage() {
  useEffect(() => {
    captureUtmsOnce();
  }, []);
  return (
    <>
      <TopBar />
      <ScrollProgress />
      <main className="min-h-screen bg-[#F7F6F3] text-[#1C1C1E]">
        {/* 1. HERO */}
        <HeroSection />
        {/* 2. IDENTIFICAÇÃO */}
        <IdentificacaoSection />
        <Suspense fallback={null}>
          {/* 3. OFERTA */}
          <OfertaSection />
          {/* 4. CONDIÇÕES ATENDIDAS */}
          <CondicoesSection />
          {/* 5. COMO FUNCIONA */}
          <ComoFuncionaSection />
          {/* 6. ATENDIMENTO POR VÍDEO */}
          <TelemedicinaSection />
          {/* 7. SOBRE O DR. ULISSES */}
          <SobreSection />
          {/* 7. AVALIAÇÕES DO GOOGLE */}
          <AvaliacoesSection />
          {/* 8. FAQ + CTA FINAL */}
          <FaqSection />
          {/* 8. RODAPÉ */}
          <FooterSection />
        </Suspense>

        <StickyMobileCTA />
        {/* Espaço-reserva para o CTA sticky mobile — evita sobreposição do conteúdo final */}
        <div className="md:hidden h-20" aria-hidden="true" />
      </main>
    </>
  );
}
